import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useApi } from '../hooks/useApi';
import { PROPERTY_TYPES, REGIONS } from '../lib/utils';
import { ClipboardList, Send, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Intake() {
  const { get, loading } = useApi();
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formId, setFormId] = useState('default');
  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  useEffect(() => {
    const fetchForm = async () => {
      try {
        // Sends auth when signed in so the account-owned form is returned
        const response = await axios.get(`${API_URL}/intake-forms/active`, authHeaders());
        setFormFields(response.data.fields || []);
        setFormId(response.data.id || 'default');
        // Initialize form data
        const initialData = {};
        response.data.fields?.forEach(field => {
          initialData[field.name] = '';
        });
        setFormData(initialData);
      } catch (error) {
        console.error('Failed to fetch form:', error);
      }
    };
    fetchForm();
  }, []);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/intake-submissions`, {
        form_id: formId,
        company_name: formData.company_name || '',
        contact_email: formData.contact_email || '',
        data: formData,
      }, authHeaders());
      setSubmitted(true);
      toast.success('Intake form submitted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-16 text-center animate-fade-in" data-testid="intake-success">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </div>
          <h1 className="font-serif text-3xl mb-4">Thank You</h1>
          <p className="text-muted-foreground mb-8">
            Your intake form has been submitted successfully. Our team will review your submission and reach out shortly.
          </p>
          <Button onClick={() => {
            setSubmitted(false);
            setFormData({});
          }} className="rounded-sm" data-testid="submit-another-btn">
            Submit Another
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8 animate-fade-in" data-testid="intake-page">
        {/* Header */}
        <div className="text-center">
          <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="h-6 w-6 text-primary" />
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl mb-2">Premium Intake</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Submit your hospitality project details and our team will create a tailored proposal.
          </p>
        </div>

        {/* Form */}
        <Card className="rounded-sm border">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="intake-form">
              {formFields.map((field) => (
                <div key={field.name} className="space-y-2">
                  <Label htmlFor={field.name}>
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  
                  {field.type === 'text' && (
                    <Input
                      id={field.name}
                      value={formData[field.name] || ''}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      required={field.required}
                      className="rounded-sm"
                      data-testid={`intake-${field.name}`}
                    />
                  )}
                  
                  {field.type === 'email' && (
                    <Input
                      id={field.name}
                      type="email"
                      value={formData[field.name] || ''}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      required={field.required}
                      className="rounded-sm"
                      data-testid={`intake-${field.name}`}
                    />
                  )}
                  
                  {field.type === 'tel' && (
                    <Input
                      id={field.name}
                      type="tel"
                      value={formData[field.name] || ''}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      required={field.required}
                      className="rounded-sm"
                      data-testid={`intake-${field.name}`}
                    />
                  )}
                  
                  {field.type === 'number' && (
                    <Input
                      id={field.name}
                      type="number"
                      value={formData[field.name] || ''}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      required={field.required}
                      className="rounded-sm"
                      data-testid={`intake-${field.name}`}
                    />
                  )}
                  
                  {field.type === 'select' && (
                    <Select
                      value={formData[field.name] || ''}
                      onValueChange={(v) => handleChange(field.name, v)}
                      required={field.required}
                    >
                      <SelectTrigger className="rounded-sm" data-testid={`intake-${field.name}`}>
                        <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map(option => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {field.type === 'textarea' && (
                    <Textarea
                      id={field.name}
                      value={formData[field.name] || ''}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      required={field.required}
                      rows={4}
                      className="rounded-sm resize-none"
                      data-testid={`intake-${field.name}`}
                    />
                  )}
                </div>
              ))}

              <Button
                type="submit"
                className="w-full rounded-sm h-12 uppercase tracking-widest text-xs"
                disabled={submitting}
                data-testid="intake-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Intake
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
